/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { createFilter, components } from 'react-select';
import { useTheme } from 'styled-components';
import { getIn } from 'formik';
import { FaCaretDown } from 'react-icons/fa';
import Flag from 'components/Flag';
import InputErrorMessage from 'products/nomad_insurance/components/InputErrorMessage';
import { Label, StyledOption, StyledSingleValue, ControlInner } from './styled';

const CustomControl = ({ children, selectProps, ...props }) => {
  return (
    <components.Control {...props}>
      {selectProps.label && <Label>{selectProps.label}</Label>}
      <ControlInner>{children}</ControlInner>
    </components.Control>
  );
};

const CustomOption = ({ data, selectProps, ...props }) => {
  return (
    <components.Option {...props}>
      <StyledOption
        selected={selectProps.value?.value === data.value}
        variant={selectProps.variant}
      >
        {selectProps.showFlag && <Flag size={16} code={data.value} />}
        {selectProps.showLabel && data.label}
      </StyledOption>
    </components.Option>
  );
};

const CustomSingleValue = ({ data, selectProps }) => {
  return (
    <StyledSingleValue data={data} hasLabel={!!selectProps.label}>
      {selectProps.showFlag && <Flag size={16} code={data.value} />}
      {data.label}
    </StyledSingleValue>
  );
};

const CustomDropdownIndicator = ({ selectProps }) => {
  if (selectProps.disabled || !selectProps.showIndicator) return null;
  return (
    <FaCaretDown
      style={{
        fill: 'currentColor',
        transform: selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
        transition: '200ms'
      }}
    />
  );
};

const Select = ({
  options,
  onChange,
  value,
  disabled,
  formik,
  name,
  label,
  showIndicator,
  showFlag,
  showLabel = true,
  hasError,
  errorMessage,
  variant = 'fountainBlue',
  onBlur,
  hideSelection,
  className
}) => {
  const theme = useTheme();

  const error =
    errorMessage || (formik && getIn(formik.touched, name) && getIn(formik.errors, name));

  const selectedValue = formik
    ? options.find((option) => option.value === getIn(formik.values, name)) || ''
    : options.find((option) => option.value === value || option.label === value);

  const handleChange = (e) => {
    if (formik) {
      formik.setFieldValue(name, e.value);
      return;
    }
    onChange(e);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
    if (formik) {
      formik.setTouched({ ...formik.touched, [name]: true });
    }
  };

  return (
    <div className={className}>
      <ReactSelect
        value={hideSelection ? null : selectedValue}
        label={label}
        filterOption={createFilter({ ignoreAccents: false })}
        onChange={handleChange}
        onBlur={handleBlur}
        options={options}
        isDisabled={disabled}
        showIndicator={showIndicator}
        showFlag={showFlag}
        showLabel={showLabel}
        variant={variant}
        components={{
          SingleValue: CustomSingleValue,
          DropdownIndicator: CustomDropdownIndicator,
          Option: CustomOption,
          Control: CustomControl
        }}
        placeholder=""
        styles={{
          option: (base) => ({
            ...base,
            backgroundColor: theme.colors.inputs[variant]?.base,
            '&:active': {
              backgroundColor: theme.colors.inputs[variant]?.base
            }
          }),
          container: (base, state) => {
            const regularColors = {
              backgroundColor: theme.colors.inputs[variant]?.base,
              outline:
                state.isFocused && variant !== 'gray'
                  ? `4px solid ${theme.colors.inputs[variant]?.outline}`
                  : 'none',
              border:
                state.isFocused && variant !== 'gray'
                  ? `1px solid ${theme.colors.inputs[variant]?.border}`
                  : '1px solid transparent'
            };

            const errorColors = {
              backgroundColor: theme.colors['fuzzyWuzzyBrown+4'],
              border: `1px solid ${theme.colors.fuzzyWuzzyBrown}`,
              outline: '4px solid rgb(199, 99, 88, 0.5)'
            };

            const colors = hasError || error ? errorColors : regularColors;

            return {
              ...base,
              boxSizing: 'border-box',
              flex: '1',
              borderRadius: '16px',
              padding: '6px 0',
              position: 'relative',
              height: '56px',
              cursor: 'pointer',
              opacity: disabled ? '0.75' : '1',
              ...colors
            };
          },
          input: () => ({
            position: 'absolute',
            top: '0',
            left: '0',
            lineHeight: '22px',
            color: theme.colors.black,
            padding: '0px',
            margin: '0px',
            fontSize: '16px',
            textIndent: 0
          }),
          control: () => ({
            borderColor: theme.colors.transparent,
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '16px',
            height: '100%',
            padding: '8px 12px',
            overflow: 'initial'
          }),
          indicatorsContainer: () => ({
            position: 'absolute',
            right: '16px',
            top: '55%',
            transform: 'translateY(-50%)'
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: theme.colors.inputs[variant]?.base,
            border: 'none',
            marginTop: '8px',
            borderRadius: '16px',
            overflow: 'hidden'
          }),
          singleValue: (base) => ({
            ...base,
            overflow: 'initial'
          })
        }}
      />
      {error && <InputErrorMessage errorMessage={error} />}
    </div>
  );
};

Select.propTypes = {
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  formik: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  showFlag: PropTypes.bool,
  showLabel: PropTypes.bool,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  variant: PropTypes.oneOf(['fountainBlue', 'gray', 'white', 'transparentWhite']),
  hideSelection: PropTypes.bool
};

export default Select;
